// @flow
import { socketRoot } from './conf'
import qs from 'query-string'
import { fixedEncodeURIComponent } from 'utility'
import { Auth } from 'services'
import { Observable, of, Subject } from 'rxjs'
import { webSocket } from 'rxjs/webSocket'
import { map, filter, catchError, tap } from 'rxjs/operators'
import { WebSocketSubject } from 'rxjs/internal/observable/dom/WebSocketSubject'
import _ from 'lodash'

/**
 * Events typename - contains observables exposed by a socket.
 *
 * @private
 * @memberof SocketRx
 */
type Events = {
  onCreate: Observable,
  onUpdate: Observable,
  onDelete: Observable,
  onError: Observable,
  onClose: Observable
}

/**
 * Socket typename - Socket is a type stores in openSockets array in order to keep track of opended sockets
 * and avoid duplicates.
 *
 * @private
 * @memberof SocketRx
 */
type Socket = {
  typename: string,
  kql: string,
  websocket: WebSocketSubject,
  closeSubject: Subject
}

/**
 * openSockets is used to prevent duplicate socket connection (uniqueness defined by typename, and kql).
 *
 * @private
 * @memberof SocketRx
 */
let openSockets: Array<Socket> = []

const getSocketIfOpen = (typename: string, kql: string): ?Socket => {
  return _.find(openSockets, { typename: typename, kql: kql })
}

/**
 * Generic SocketRx implementation with a socket caching mechanism preventing from opening multiple sockets of the same
 * type simultaneously.
 *
 * @function
 * @namespace SocketRx
 * @version 1.0.0
 * @description
 * Makes a socket connection provided a typename and kql string.
 * Allows subscribing to:
 *   - create
 *   - update
 *   - delete
 *
 * @return { Events } Exposes `onCreate`, `onUpdate` `onDelete` observables to subscribe to.
 */
const open = (typename: string, kql: string): Events => {
  const query = {
    typename: typename,
    token: Auth.getToken()
  }

  let ws = null
  let closeEvents = null
  try {
    let socket = getSocketIfOpen(typename, kql)
    ws = socket ? socket.websocket : null
    closeEvents = socket ? socket.closeSubject : null
  } catch (err) {
    let erroredSockets = _.filter(openSockets, { typename: typename, kql: kql })
    erroredSockets.forEach(f => {
      f.websocket.complete()
    })
    _.remove(openSockets, { typename: typename, kql: kql })
    console.error(err)
  }

  if (!ws) {
    closeEvents = new Subject()
    closeEvents = closeEvents.pipe(
      tap(evt => {
        console.log('Socket closed', typename, kql, evt)
        _.remove(openSockets, { typename: typename, kql: kql })
      })
    )
    ws = webSocket({
      url: `${socketRoot}?${qs.stringify(query)}&kql=${fixedEncodeURIComponent(kql)}`,
      closeObserver: closeEvents,
      openObserver: {
        next: openEvent => {
          console.log('Socket open', typename, kql, openEvent)
        },
        error: err => {
          _.remove(openSockets, { typename: typename, kql: kql })
          console.log('Socket error', err)
        }
      }
    })

    openSockets.push({ typename: typename, kql: kql, websocket: ws, closeSubject: closeEvents })
  }

  let events = ws.pipe(
    map(data => {
      if (data.new && !data.old) return { event: 'create', data: data.new }
      if (data.new && data.old) return { event: 'update', data: data.new }
      if (!data.new && data.old) return { event: 'delete', data: data.old }
      else {
        console.log('Unrecognized event in SocketRx', data)
        return {}
      }
    }),
    catchError(err => {
      return of({ event: 'error', data: err })
    })
  )

  let createEvents = events.pipe(
    filter(e => e.event === 'create'),
    map(event => event.data)
  )
  let updateEvents = events.pipe(
    filter(e => e.event === 'update'),
    map(event => event.data)
  )
  let deleteEvents = events.pipe(
    filter(e => e.event === 'delete'),
    map(event => event.data)
  )
  let errorEvents = events.pipe(
    filter(e => e.event === 'error'),
    map(event => event.data)
  )

  return {
    onCreate: createEvents.asObservable(),
    onUpdate: updateEvents.asObservable(),
    onDelete: deleteEvents.asObservable(),
    onError: errorEvents.asObservable(),
    onClose: closeEvents ? closeEvents.asObservable() : of([])
  }
}

export default {
  open
}
